export interface SelectionData {
  range: Range;
  text: string;
  html: string;
}

/**
 * Captures the current browser text selection within a container and returns
 * a snapshot of the Range object, selected text, and HTML content.
 *
 * Why we need this:
 *
 * The browser's Selection API is "live" - the Range object changes as the user
 * modifies their selection. This creates a critical problem:
 *
 * 1. User selects text → AI toolbar appears
 * 2. User clicks elsewhere (selection changes/disappears)
 * 3. User clicks "Apply AI correction"
 * 4. ❌ Original selection is lost!
 *
 * This function solves the problem by:
 * - Cloning the Range (creating a frozen snapshot of DOM positions)
 * - Capturing the plain text at the exact same moment
 * - Capturing the HTML content to preserve formatting (bold, italic, links, etc.)
 * - Validating that the selection is valid and within our editor
 *
 * The cloned Range can later be used to restore and replace the exact original
 * selection, even if the user has since changed or cleared their selection.
 *
 * @param container - The editor container element to validate selection is within
 * @returns Object with cloned Range, text, and HTML, or null if selection is invalid
 */
export function captureSelectionData(
  container: HTMLDivElement | null,
): SelectionData | null {
  if (!container) return null;

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const selectedText = selection.toString();

  if (
    !selection.isCollapsed &&
    container.contains(range.commonAncestorContainer) &&
    selectedText.trim().length > 0
  ) {
    const fragment = range.cloneContents();
    const div = document.createElement('div');
    div.appendChild(fragment);
    const selectedHtml = div.innerHTML;

    return {
      range: range.cloneRange(),
      text: selectedText,
      html: selectedHtml,
    };
  }

  return null;
}
